import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { AdminCohortCoach } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  Avatar,
  AvatarGroup,
  Box,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { cohortCoachInitial, cohortCoachLabel } from './cohortCoaches';

const { coachesColumn: strings } = appStrings.modules.rostersAdmin;

/** Past this many the group carries a "+N" instead, counted off the same list it draws from. */
const AVATARS_SHOWN = 5;

const tooltipContent = (coaches: readonly AdminCohortCoach[]) => (
  <Box sx={tooltipListSx}>
    {coaches.map((coach) => (
      <Typography key={coach.id} variant="text-xs-regular" sx={inheritColourSx}>
        {cohortCoachLabel(coach)}
      </Typography>
    ))}
  </Box>
);

type CohortCoachesCellProps = Readonly<{
  id: string;
  coaches: readonly AdminCohortCoach[];
}>;

/**
 * The group takes focus so its list is reachable without a pointer: the names are only
 * otherwise readable from a hover tooltip.
 */
export const CohortCoachesCell = ({ id, coaches }: CohortCoachesCellProps) => (
  <Tooltip
    title={tooltipContent(coaches)}
    placement="top"
    arrow
    disableHoverListener={coaches.length === 0}
    disableFocusListener={coaches.length === 0}
    slotProps={{ tooltip: { sx: tooltipSx }, arrow: { sx: tooltipArrowSx } }}
  >
    <AvatarGroup
      id={id}
      max={AVATARS_SHOWN}
      total={coaches.length}
      sx={avatarGroupSx}
      tabIndex={0}
      aria-label={
        coaches.length === 0 ? strings.none : strings.list(coaches.map(cohortCoachLabel).join(', '))
      }
    >
      {coaches.map((coach) => (
        <Avatar
          key={coach.id}
          src={coach.profileImageUrl ?? undefined}
          alt={cohortCoachLabel(coach)}
        >
          {cohortCoachInitial(coach)}
        </Avatar>
      ))}
    </AvatarGroup>
  </Tooltip>
);

const avatarGroupSx: SxProps<Theme> = ({ backrs, typography }) => ({
  justifyContent: 'flex-start',
  width: 'fit-content',
  '& .MuiAvatar-root': {
    width: 24,
    height: 24,
    ...typography['text-xs-semibold'],
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});

const tooltipListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const inheritColourSx: SxProps<Theme> = { color: 'inherit' };

const tooltipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary-solid'],
  padding: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
});

const tooltipArrowSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Background/bg-primary-solid'],
});
